import { useState, useEffect, useCallback } from "react";
import { Wallet, Coins, ArrowUpRight, ArrowDownRight, Plus, Package } from "lucide-react";
import MetricCard from "@/components/MetricCard";
import { Money, Credits } from "@/components/Currency";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Badge } from "@/components/ui/badge";
import RechargeDialog from "@/components/RechargeDialog";
import SupplierPixDialog from "@/components/SupplierPixDialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TYPE_LABEL = {
  admin_adjust: "Ajuste admin", activation_reserve: "Ativação", activation_refund: "Estorno",
  activation_consume: "Ativação", transfer_in: "Recebido", transfer_out: "Enviado",
  purchase: "Compra de créditos", recharge: "Recarga de saldo",
};

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

export default function UserCredits() {
  const { user, refreshUser } = useAuth();
  const [options, setOptions] = useState({ balance_topups: [], credit_packages: [] });
  const [movements, setMovements] = useState([]);
  const [payload, setPayload] = useState(null);
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [supplierPixOpen, setSupplierPixOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);

  const loadMovements = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([
        api.get("/credits/ledger?limit=15"),
        api.get("/credits/balance-ledger?limit=15"),
      ]);
      const cred = (c.data.items || []).map((x) => ({ ...x, asset: "credits" }));
      const bal = (b.data.items || []).map((x) => ({ ...x, asset: "balance" }));
      const merged = [...cred, ...bal]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 20);
      setMovements(merged);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/pix/recharge/options").then(({ data }) => setOptions(data)).catch(() => {});
    api.get("/reseller/supplier").then(({ data }) => setSupplier(data)).catch(() => {});
    loadMovements();
  }, [loadMovements]);

  const openRecharge = (p) => { setPayload(p); setOpen(true); };
  const onSuccess = () => { refreshUser(); loadMovements(); };
  const buyWithSaldo = async (p) => {
    try {
      await api.post("/reseller/buy-package", { package_id: p.id, method: "saldo" });
      toast.success(`+${p.credits} créditos adicionados!`); onSuccess();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="user-credits">
      <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Carteira</h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <MetricCard icon={Wallet} label="Saldo (R$)" value={<Money value={user?.balance} testid="wallet-balance-value" />} testid="wallet-balance" />
        <MetricCard icon={Coins} label="Créditos" value={<Credits value={user?.credits ?? 0} testid="wallet-credits-badge" />} testid="wallet-credits" />
      </div>

      {/* Add balance */}
      <div>
        <h3 className="font-heading text-lg font-semibold">Adicionar saldo</h3>
        <p className="text-sm text-muted-foreground">Recarregue via Pix e use o saldo em qualquer ativação.</p>
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-9">
          {options.balance_topups.map((v) => (
            <Button
              key={v}
              variant="outline"
              className="h-14 flex-col gap-0.5 font-num text-base font-bold hover:border-primary hover:text-primary"
              data-testid={`topup-${v}`}
              onClick={() => openRecharge({ kind: "balance", amount: v, label: `Adicionar R$ ${brl(v)} de saldo` })}
            >
              <span className="text-[10px] font-normal uppercase tracking-wide text-muted-foreground">R$</span>
              {v}
            </Button>
          ))}
        </div>
      </div>

      {/* Buy credits */}
      {supplier?.supplier_type === "reseller" ? (
        <div>
          <h3 className="font-heading text-lg font-semibold">Comprar créditos de {supplier.reseller_name}</h3>
          <p className="text-sm text-muted-foreground">Pague com seu saldo (R$) ou via Pix no gateway do seu revendedor.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {supplier.packages.length === 0 && (
              <div className="col-span-full flex items-center gap-2 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                <Package className="h-5 w-5" /> Seu revendedor ainda não cadastrou pacotes.
              </div>
            )}
            {supplier.packages.map((p) => (
              <div key={p.id} className="flex flex-col rounded-xl border border-border bg-card p-5" data-testid={`supplier-package-${p.id}`}>
                <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{p.name}</span>
                <p className="mt-2 font-num text-3xl font-bold text-primary">{p.credits}</p>
                <p className="text-xs text-muted-foreground">créditos</p>
                <p className="mt-3 font-num text-xl font-semibold">R$ {brl(p.price)}</p>
                <div className="mt-4 flex flex-col gap-2">
                  <Button variant="outline" className="gap-1.5" data-testid={`buy-saldo-${p.id}`} onClick={() => buyWithSaldo(p)}>
                    <Wallet className="h-4 w-4" /> Pagar com saldo
                  </Button>
                  <Button className="gap-1.5" disabled={!supplier.has_pix} data-testid={`buy-pix-${p.id}`}
                    onClick={() => { setSelectedPkg(p); setSupplierPixOpen(true); }}>
                    <Plus className="h-4 w-4" /> {supplier.has_pix ? "Pagar via Pix" : "Pix indisponível"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <h3 className="font-heading text-lg font-semibold">Comprar créditos</h3>
          <p className="text-sm text-muted-foreground">Pacotes de revenda — quanto maior, menor o custo por crédito.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {options.credit_packages.length === 0 && (
              <div className="col-span-full flex items-center gap-2 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                <Package className="h-5 w-5" /> Nenhum pacote disponível no momento.
              </div>
            )}
            {options.credit_packages.map((p) => (
              <div key={p.id} className="flex flex-col rounded-xl border border-border bg-card p-5" data-testid={`package-${p.id}`}>
                <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{p.name}</span>
                <p className="mt-2 font-num text-3xl font-bold text-primary">{p.credits}</p>
                <p className="text-xs text-muted-foreground">créditos</p>
                <p className="mt-3 font-num text-xl font-semibold">R$ {brl(p.price)}</p>
                <Button className="mt-4 gap-1.5 glow-red" data-testid={`buy-package-${p.id}`}
                  onClick={() => openRecharge({ kind: "credits", package_id: p.id, label: `Comprar ${p.credits} créditos` })}>
                  <Plus className="h-4 w-4" /> Comprar via Pix
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Movements */}
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border p-4"><h3 className="font-heading font-semibold">Movimentações</h3></div>
        <div className="p-3 md:hidden">
          <MobileCards testid="ledger-cards">
            {movements.length === 0 && <EmptyCard>Sem movimentações.</EmptyCard>}
            {movements.map((l) => (
              <DataCard
                key={`m-${l.asset}-${l.id}`}
                testid={`ledger-card-${l.id}`}
                title={TYPE_LABEL[l.type] || l.type}
                subtitle={l.description}
                badge={<Badge variant={l.asset === "balance" ? "default" : "secondary"} className="text-[10px]">{l.asset === "balance" ? "Saldo" : "Crédito"}</Badge>}
                rows={[
                  {
                    label: "Valor",
                    value: (
                      <span className={`inline-flex items-center gap-1 font-num font-semibold ${l.amount >= 0 ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
                        {l.amount >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        {l.amount >= 0 ? "+" : ""}{l.asset === "balance" ? `R$ ${brl(Math.abs(l.amount))}` : Math.abs(l.amount)}
                      </span>
                    ),
                  },
                  { label: "Depois", value: <span className="font-num">{l.asset === "balance" ? `R$ ${brl(l.balance_after)}` : l.balance_after}</span> },
                  { label: "Data", value: new Date(l.created_at).toLocaleString("pt-BR"), full: true },
                ]}
              />
            ))}
          </MobileCards>
        </div>
        <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Saldo/Depois</TableHead>
              <TableHead>Data</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Sem movimentações.</TableCell></TableRow>}
            {movements.map((l) => (
              <TableRow key={`${l.asset}-${l.id}`} data-testid={`ledger-row-${l.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant={l.asset === "balance" ? "default" : "secondary"} className="text-[10px]">{l.asset === "balance" ? "Saldo" : "Crédito"}</Badge>
                    {TYPE_LABEL[l.type] || l.type}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{l.description}</TableCell>
                <TableCell className={`text-right font-num font-medium ${l.amount >= 0 ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
                  <span className="inline-flex items-center gap-1">
                    {l.amount >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {l.amount >= 0 ? "+" : ""}{l.asset === "balance" ? `R$ ${brl(Math.abs(l.amount))}` : Math.abs(l.amount)}
                  </span>
                </TableCell>
                <TableCell className="text-right font-num">{l.asset === "balance" ? `R$ ${brl(l.balance_after)}` : l.balance_after}</TableCell>
                <TableCell className="text-muted-foreground">{new Date(l.created_at).toLocaleString("pt-BR")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <RechargeDialog open={open} onOpenChange={setOpen} payload={payload} onSuccess={onSuccess} />
      <SupplierPixDialog open={supplierPixOpen} onOpenChange={setSupplierPixOpen} pkg={selectedPkg}
        needCpf={supplier?.pix_provider === "asaas"} onDone={onSuccess} />
    </div>
  );
}
